import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import { FiArrowRight, FiAlertCircle, FiCheckCircle, FiSend } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

export default function WithdrawalRequestScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [balance, setBalance] = useState(0);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ canWithdraw: true, daysLeft: 0 });

  useEffect(() => {
    checkStatus();
  }, [user]);

  const checkStatus = async () => {
    if (!user) return;
    try {
        // جلب الرصيد
        const provider = await supabaseService.getProviderById(user.id);
        setBalance(provider?.wallet || 0);

        // التحقق من آخر طلب
        const lastReq = await supabaseService.getLastWithdrawalRequest(user.id);
        if (lastReq && lastReq.created_at) {
            const lastDate = new Date(lastReq.created_at);
            const diffDays = Math.floor((new Date() - lastDate) / (1000 * 60 * 60 * 24));
            if (diffDays < 7) {
                setStatus({ canWithdraw: false, daysLeft: 7 - diffDays });
            }
        }
    } catch (e) {
        console.error(e);
    }
  };

  const handleSubmit = async () => {
    const val = parseFloat(amount);
    if (!val || val < 4000) return toast.error('الحد الأدنى للسحب هو 4,000 دج');
    if (val > balance) return toast.error('المبلغ أكبر من رصيدك');
    if (!status.canWithdraw) return toast.error(`عذراً، يجب الانتظار ${status.daysLeft} أيام`);

    setLoading(true);
    try {
        await supabaseService.createWithdrawalRequest({ providerId: user.id, amount: val });
        toast.success('تم إرسال طلب السحب بنجاح!');
        navigate(-1);
    } catch (e) {
        toast.error(e.message);
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="h-screen w-full bg-gray-50 font-sans flex flex-col overflow-hidden" dir="rtl">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 lg:px-10 py-3 flex items-center gap-3 shadow-sm shrink-0">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200">
                <FiArrowRight className="text-gray-700" />
            </button>
            <h1 className="text-lg font-bold text-gray-900">طلب سحب</h1>
        </div>

        {/* Main content — fills remaining height, no scroll */}
        <div className="flex-1 min-h-0 px-6 lg:px-10 py-5 lg:py-8 flex items-center justify-center">
            <div className="w-full max-w-4xl grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">

                {/* ── العمود الأيمن: الرصيد + الحالة ── */}
                <div className="lg:col-span-2 flex flex-col gap-5">
                    {/* بطاقة الرصيد */}
                    <div className="bg-black text-white p-6 rounded-2xl shadow-xl flex-1 flex flex-col justify-center">
                        <p className="text-xs text-gray-400 mb-1">رصيدك الحالي</p>
                        <div className="flex items-end gap-2">
                            <h1 className="text-4xl font-extrabold">{balance.toFixed(0)}</h1>
                            <span className="text-gray-300 mb-1">دج</span>
                        </div>
                    </div>

                    {/* حالة السحب */}
                    {!status.canWithdraw ? (
                        <div className="bg-red-50 border border-red-200 p-4 rounded-xl flex items-center gap-3 text-red-700">
                            <FiAlertCircle size={20} className="shrink-0" />
                            <div>
                                <p className="font-bold">غير متاح حالياً</p>
                                <p className="text-xs">متبقي {status.daysLeft} أيام لطلب السحب القادم.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-green-50 border border-green-200 p-4 rounded-xl flex items-center gap-3 text-green-700">
                            <FiCheckCircle size={20} className="shrink-0" />
                            <div>
                                <p className="font-bold">يمكنك السحب الآن</p>
                                <p className="text-xs">طلب واحد كل 7 أيام.</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── العمود الأيسر: نموذج السحب ── */}
                <div className="lg:col-span-3 flex flex-col justify-center">
                    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 lg:p-8">
                        {status.canWithdraw ? (
                            <div className="space-y-5">
                                <div>
                                    <label className="text-sm text-gray-600 mb-2 block">المبلغ المراد سحبه (الحد الأدنى 4,000 دج)</label>
                                    <input
                                        type="number"
                                        value={amount}
                                        onChange={e => setAmount(e.target.value)}
                                        className="w-full p-4 border-2 border-gray-200 rounded-xl text-2xl font-bold focus:border-black outline-none"
                                        placeholder="0.00"
                                    />
                                </div>

                                <button onClick={handleSubmit} disabled={loading} className="w-full bg-black text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-95 transition">
                                    {loading ? 'جاري الإرسال...' : <><FiSend /> إرسال الطلب</>}
                                </button>
                            </div>
                        ) : (
                            <div className="text-center py-6 text-gray-400">
                                <FiAlertCircle className="mx-auto mb-3" size={28} />
                                <p className="text-sm">لا يمكنك إرسال طلب سحب جديد حالياً.</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    </div>
  );
}